import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const Cruises = () => {
  const [cruises, setCruises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchCruises = async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('cruises')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      setCruises(data || []);
    } catch (err) {
      console.error('Error fetching cruises:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCruises();
    
    // Подписываемся на изменения в таблице cruises
    const subscription = supabase
      .channel('cruises-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'cruises' }, 
        () => {
          console.log('Cruises table changed, refreshing...');
          fetchCruises();
        }
      )
      .subscribe();
    
    return () => {
      subscription.unsubscribe();
    };
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteCruise = async (id, title) => {
    if (!window.confirm(`Удалить круиз "${title}"? Это действие нельзя отменить.`)) return;
    
    try {
      // Сначала получаем круиз, чтобы удалить изображения из Storage
      const { data: cruise, error: fetchError } = await supabase
        .from('cruises')
        .select('image_url, gallery_images')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Удаляем главное изображение
      if (cruise.image_url) {
        const imagePath = cruise.image_url.split('/').pop();
        if (imagePath) {
          await supabase.storage
            .from('tour-images')
            .remove([`main/${imagePath}`]);
        }
      }
      
      // Удаляем изображения из галереи
      if (cruise.gallery_images && cruise.gallery_images.length > 0) {
        for (const url of cruise.gallery_images) {
          const imagePath = url.split('/').pop();
          if (imagePath) {
            await supabase.storage
              .from('tour-images')
              .remove([`gallery/${imagePath}`]);
          }
        }
      }
      
      // Удаляем круиз из базы
      const { error: deleteError } = await supabase
        .from('cruises')
        .delete()
        .eq('id', id);
      
      if (deleteError) throw deleteError;
      
      alert('Круиз успешно удален');
    } catch (err) {
      console.error('Error deleting cruise:', err);
      alert('Ошибка при удалении круиза: ' + err.message);
    }
  };

  const archiveCruise = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'archived' : 'active';
    const action = newStatus === 'active' ? 'восстановлен' : 'архивирован';
    
    if (!window.confirm(`Вы уверены, что хотите ${action} этот круиз?`)) return;
    
    try {
      const { error } = await supabase
        .from('cruises')
        .update({ status: newStatus })
        .eq('id', id);
      
      if (error) throw error;
      
      alert(`Круиз успешно ${action}`);
    } catch (err) {
      console.error('Error updating cruise status:', err);
      alert('Ошибка при изменении статуса круиза: ' + err.message);
    }
  };

  const archiveExpiredCruises = async () => {
    if (!window.confirm('Архивировать все круизы с истекшим сроком бронирования?')) return;
    
    try {
      const { error } = await supabase.rpc('archive_expired_cruises');
      
      if (error) throw error;
      
      alert('Истекшие круизы архивированы');
      fetchCruises();
    } catch (err) {
      console.error('Error archiving expired cruises:', err);
      alert('Ошибка: ' + err.message);
    }
  };

  const getDaysRemaining = (deadlineDate) => {
    if (!deadlineDate) return null;
    const days = Math.ceil((new Date(deadlineDate) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'active':
        return {
          text: 'Активен',
          style: {
            backgroundColor: '#d1fae5',
            color: '#065f46',
            padding: '0.25rem 0.5rem',
            borderRadius: '0.25rem',
            fontSize: '0.75rem',
            fontWeight: '500'
          }
        };
      case 'archived':
        return {
          text: 'В архиве',
          style: {
            backgroundColor: '#f3f4f6',
            color: '#6b7280',
            padding: '0.25rem 0.5rem',
            borderRadius: '0.25rem',
            fontSize: '0.75rem',
            fontWeight: '500'
          }
        };
      default:
        return { text: status, style: {} };
    }
  };

  if (loading && cruises.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '18px',
        color: '#6b7280'
      }}>
        <div>Загрузка круизов...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '2rem',
        color: '#dc2626',
        backgroundColor: '#fee2e2',
        borderRadius: '0.5rem',
        margin: '1rem'
      }}>
        <p>Ошибка загрузки круизов: {error}</p>
        <button 
          onClick={fetchCruises}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: 'pointer'
          }}
        >
          Повторить попытку
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
          Круизы
          <span style={{ 
            fontSize: '0.875rem', 
            color: '#6b7280', 
            fontWeight: 'normal',
            marginLeft: '0.5rem'
          }}>
            ({cruises.length} {cruises.length === 1 ? 'круиз' : cruises.length < 5 ? 'круиза' : 'круизов'})
          </span>
        </h1>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              backgroundColor: 'white'
            }}
          >
            <option value="all">Все статусы</option>
            <option value="active">Активные</option>
            <option value="archived">В архиве</option>
          </select>
          <button
            onClick={archiveExpiredCruises}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer'
            }}
          >
            📦 Архивировать истекшие
          </button>
          <Link
            to="/admin/cruises/new"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#3b82f6',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '0.375rem',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
          >
            <span style={{ fontSize: '1.25rem' }}>+</span>
            Добавить круиз
          </Link>
        </div>
      </div>
      
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflowX: 'auto'
      }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          minWidth: '800px'
        }}>
          <thead style={{ backgroundColor: '#f9fafb' }}>
            <tr>
              <th style={{ 
                padding: '0.75rem 1rem', 
                textAlign: 'left', 
                fontSize: '0.75rem', 
                fontWeight: '500', 
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Изображение
              </th>
              <th style={{ 
                padding: '0.75rem 1rem', 
                textAlign: 'left', 
                fontSize: '0.75rem', 
                fontWeight: '500', 
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Название
              </th>
              <th style={{ 
                padding: '0.75rem 1rem', 
                textAlign: 'left', 
                fontSize: '0.75rem', 
                fontWeight: '500', 
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Корабль
              </th>
              <th style={{ 
                padding: '0.75rem 1rem', 
                textAlign: 'left', 
                fontSize: '0.75rem', 
                fontWeight: '500', 
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Дата
              </th>
              <th style={{ 
                padding: '0.75rem 1rem', 
                textAlign: 'left', 
                fontSize: '0.75rem', 
                fontWeight: '500', 
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Цена
              </th>
              <th style={{ 
                padding: '0.75rem 1rem', 
                textAlign: 'left', 
                fontSize: '0.75rem', 
                fontWeight: '500', 
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Осталось дней
              </th>
              <th style={{ 
                padding: '0.75rem 1rem', 
                textAlign: 'left', 
                fontSize: '0.75rem', 
                fontWeight: '500', 
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Статус
              </th>
              <th style={{ 
                padding: '0.75rem 1rem', 
                textAlign: 'right', 
                fontSize: '0.75rem', 
                fontWeight: '500', 
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Действия
              </th>
            </tr>
          </thead>
          <tbody style={{ borderTop: '1px solid #e5e7eb' }}>
            {cruises.map((cruise) => {
              const statusBadge = getStatusBadge(cruise.status);
              return (
                <tr key={cruise.id} style={{ borderBottom: '1px solid #e5e7eb', transition: 'background-color 0.2s' }}>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <img
                      src={cruise.image_url || 'https://via.placeholder.com/64x64?text=No+image'}
                      alt={cruise.title}
                      style={{ 
                        width: '64px', 
                        height: '64px', 
                        objectFit: 'cover', 
                        borderRadius: '0.375rem',
                        backgroundColor: '#f3f4f6'
                      }}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/64x64?text=Error';
                      }}
                    />
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: '500' }}>
                    {cruise.title}
                    {cruise.slug && (
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                        slug: {cruise.slug}
                      </div>
                    )}
                    {cruise.ship_name && (
                      <div style={{ fontSize: '0.75rem', color: '#3b82f6', marginTop: '0.25rem' }}>
                        {cruise.ship_name}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    {cruise.ship_name && (
                      <div style={{ fontSize: '0.875rem' }}>{cruise.ship_name}</div>
                    )}
                    {cruise.company_name && (
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{cruise.company_name}</div>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    {cruise.departure_date && (
                      <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                        {new Date(cruise.departure_date).toLocaleDateString('ru-RU')}
                      </div>
                    )}
                    {cruise.duration_days && (
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {cruise.duration_days}д / {cruise.duration_nights}н
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    {cruise.price_from && (
                      <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#10b981' }}>
                        {Number(cruise.price_from).toLocaleString('ru-RU')} ₽
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    {(() => {
                      const days = getDaysRemaining(cruise.deadline_date);
                      if (days === null) return <span style={{ color: '#9ca3af' }}>-</span>;
                      if (days < 0) return <span style={{ color: '#ef4444', fontWeight: '600' }}>Истек</span>;
                      if (days < 7) return <span style={{ color: '#ef4444', fontWeight: '600' }}>{days}д ⚠️</span>;
                      if (days < 30) return <span style={{ color: '#f59e0b', fontWeight: '600' }}>{days}д</span>;
                      return <span style={{ color: '#10b981' }}>{days}д</span>;
                    })()}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <span style={statusBadge.style}>
                      {statusBadge.text}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                      <Link
                        to={`/admin/cruises/${cruise.id}`}
                        style={{
                          padding: '0.5rem 0.75rem',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                      >
                        ✏️ Редактировать
                      </Link>
                      <button
                        onClick={() => archiveCruise(cruise.id, cruise.status)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          backgroundColor: cruise.status === 'active' ? '#f59e0b' : '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = cruise.status === 'active' ? '#d97706' : '#059669'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = cruise.status === 'active' ? '#f59e0b' : '#10b981'}
                      >
                        {cruise.status === 'active' ? '📦 В архив' : '🔄 Восстановить'}
                      </button>
                      <button
                        onClick={() => deleteCruise(cruise.id, cruise.title)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                      >
                        🗑️ Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {cruises.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '3rem', 
            color: '#6b7280',
            backgroundColor: '#f9fafb'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚢</div>
            <p>Нет добавленных круизов</p>
            <Link
              to="/admin/cruises/new"
              style={{
                display: 'inline-block',
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '0.375rem'
              }}
            >
              Добавить первый круиз
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cruises;
